import { css, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { BaseElement } from "../ui-kit/BaseElement";
import { designTokensCss, ensureDmSansFont } from "../ui-kit/designTokens";
import { t } from "../../translation/i18n";
import i18next from "i18next";
import {
  plannerLogin,
  plannerSelectCompany,
  storeAuthResponse,
} from "./AuthApi";
import {
  fetchRecaptchaSiteKey,
  loadRecaptchaScript,
  recaptchaResponse,
  renderRecaptchaWidget,
  resetRecaptchaWidget,
} from "./Recaptcha";
import { cancelLogin, completeLogin, registerLoginPopup } from "./ensureAuth";
import type { AuthCompanyOption } from "./AuthTypes";

@customElement("sugar-login-popup")
export class SugarLoginPopup extends BaseElement {
  static override styles = css`
    ${designTokensCss}

    :host {
      display: none;
      pointer-events: none;
    }

    :host([open]) {
      display: block;
      position: fixed;
      inset: 0;
      z-index: 10080;
      pointer-events: auto;
    }

    .overlay {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: var(--srd-space-4);
      background: rgba(15, 23, 42, 0.42);
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
      box-sizing: border-box;
    }

    .panel {
      width: min(400px, 100%);
      display: flex;
      flex-direction: column;
      gap: var(--srd-space-4);
      padding: var(--srd-space-5);
      border-radius: var(--srd-radius-lg);
      border: 1px solid var(--srd-border);
      background: var(--srd-surface-solid);
      box-shadow: var(--srd-shadow-lg);
      font-family: var(--srd-font);
      color: var(--srd-text);
      box-sizing: border-box;
    }

    .header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: var(--srd-space-3);
    }

    .title {
      margin: 0;
      font-size: 1.125rem;
      font-weight: 600;
      letter-spacing: -0.02em;
    }

    .subtitle {
      margin: var(--srd-space-1) 0 0;
      font-size: 0.8125rem;
      color: var(--srd-text-muted);
      line-height: 1.4;
    }

    .close {
      appearance: none;
      width: 32px;
      height: 32px;
      border: 0;
      border-radius: var(--srd-radius-sm);
      background: transparent;
      color: var(--srd-text-muted);
      cursor: pointer;
      font-size: 1.25rem;
      line-height: 1;
    }

    .close:hover {
      background: var(--srd-bg-muted);
      color: var(--srd-text);
    }

    form,
    .fields {
      display: flex;
      flex-direction: column;
      gap: var(--srd-space-3);
    }

    label {
      display: flex;
      flex-direction: column;
      gap: 6px;
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--srd-text-muted);
    }

    input {
      appearance: none;
      min-height: 42px;
      padding: 0 var(--srd-space-3);
      border: 1px solid var(--srd-border-strong);
      border-radius: var(--srd-radius-sm);
      background: var(--srd-bg);
      color: var(--srd-text);
      font: inherit;
      font-size: 0.875rem;
      font-weight: 500;
    }

    input:focus {
      outline: none;
      box-shadow: var(--srd-focus-ring);
      border-color: var(--srd-accent);
    }

    .error {
      margin: 0;
      font-size: 0.8125rem;
      color: var(--srd-danger);
    }

    .companies {
      display: flex;
      flex-direction: column;
      gap: var(--srd-space-2);
    }

    .company {
      appearance: none;
      min-height: 44px;
      padding: 0 var(--srd-space-3);
      border: 1px solid var(--srd-border-strong);
      border-radius: var(--srd-radius-sm);
      background: var(--srd-bg);
      color: var(--srd-text);
      font: inherit;
      font-size: 0.875rem;
      font-weight: 600;
      text-align: left;
      cursor: pointer;
    }

    .company:hover:not(:disabled) {
      background: var(--srd-view-toggle-hover-bg);
    }

    .actions {
      display: grid;
      grid-template-columns: 1fr 1.4fr;
      gap: var(--srd-space-2);
    }

    .btn {
      appearance: none;
      min-height: 42px;
      padding: 0 var(--srd-space-4);
      border-radius: var(--srd-radius-sm);
      font: inherit;
      font-size: 0.875rem;
      font-weight: 600;
      cursor: pointer;
      border: 1px solid transparent;
    }

    .btn-secondary {
      background: var(--srd-bg);
      color: var(--srd-text);
      border-color: var(--srd-border-strong);
    }

    .btn-primary {
      background: var(--srd-button);
      color: var(--srd-button-text);
    }

    .btn:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    .recaptcha {
      min-height: 78px;
    }
  `;

  @state()
  private _open = false;

  @state()
  private _identifier = "";

  @state()
  private _password = "";

  @state()
  private _error = "";

  @state()
  private _submitting = false;

  @state()
  private _selectionToken = "";

  @state()
  private _companies: AuthCompanyOption[] = [];

  @state()
  private _recaptchaSiteKey: string | null = null;

  private _recaptchaHost?: HTMLDivElement;
  private _recaptchaWidgetId: number | null = null;

  protected useShadowRoot = true;

  override connectedCallback(): void {
    super.connectedCallback();
    ensureDmSansFont();
    registerLoginPopup({
      show: () => this._setOpen(true),
      hide: () => this._setOpen(false),
    });
    this._recaptchaHost = document.createElement("div");
    this._recaptchaHost.slot = "recaptcha";
    this.appendChild(this._recaptchaHost);
  }

  override disconnectedCallback(): void {
    this._recaptchaHost?.remove();
    this._recaptchaHost = undefined;
    this._recaptchaWidgetId = null;
    super.disconnectedCallback();
  }

  override updated(): void {
    this.toggleAttribute("open", this._open);
  }

  private _setOpen(open: boolean) {
    this._open = open;
    if (open) {
      this._error = "";
      this._submitting = false;
      requestAnimationFrame(() => {
        const input = this.renderRoot.querySelector("input") as HTMLInputElement | null;
        input?.focus();
        void this._ensureRecaptcha();
      });
    } else {
      this._password = "";
      this._selectionToken = "";
      this._companies = [];
      resetRecaptchaWidget(this._recaptchaWidgetId);
    }
  }

  private _onCancel() {
    if (this._submitting) return;
    cancelLogin();
  }

  private _onKeydown(event: KeyboardEvent) {
    if (event.key === "Escape") {
      event.preventDefault();
      this._onCancel();
    }
  }

  private async _onSubmit(event: Event) {
    event.preventDefault();
    if (this._submitting) return;
    const identifier = this._identifier.trim();
    if (!identifier || !this._password) {
      this._error = i18next.t("authErrorGeneric");
      return;
    }
    this._submitting = true;
    this._error = "";
    try {
      const recaptchaToken = recaptchaResponse(this._recaptchaWidgetId);
      if (this._recaptchaSiteKey && !recaptchaToken) {
        this._error = i18next.t("authErrorRecaptcha");
        return;
      }
      const result = await plannerLogin(identifier, this._password, recaptchaToken);
      await this._handleResult(result);
    } finally {
      this._submitting = false;
    }
  }

  private async _pickCompany(companyId: string) {
    if (this._submitting || !this._selectionToken) return;
    this._submitting = true;
    this._error = "";
    try {
      const result = await plannerSelectCompany(this._selectionToken, companyId);
      await this._handleResult(result);
    } finally {
      this._submitting = false;
    }
  }

  private async _handleResult(result: {
    status: string;
    ticket?: string | null;
    selectionToken?: string | null;
    companies?: AuthCompanyOption[];
    errorCode?: string | null;
  }) {
    if (result.status === "COMPANY_SELECTION_REQUIRED") {
      this._selectionToken = result.selectionToken || "";
      this._companies = result.companies || [];
      this._password = "";
      return;
    }
    const record = storeAuthResponse(result as Parameters<typeof storeAuthResponse>[0]);
    if (record) {
      completeLogin(record);
      return;
    }
    resetRecaptchaWidget(this._recaptchaWidgetId);
    if (result.errorCode === "RECAPTCHA_FAILED") {
      this._error = i18next.t("authErrorRecaptcha");
      return;
    }
    this._error =
      result.errorCode === "INVALID_CREDENTIALS"
        ? i18next.t("authErrorInvalid")
        : i18next.t("authErrorGeneric");
  }

  private async _ensureRecaptcha() {
    if (!this._open || this._companies.length > 0 || !this._recaptchaHost) return;
    const siteKey = this._recaptchaSiteKey ?? (await fetchRecaptchaSiteKey());
    this._recaptchaSiteKey = siteKey;
    if (!siteKey) return;
    try {
      await loadRecaptchaScript();
    } catch {
      return;
    }
    if (!this._open || !this._recaptchaHost) return;
    if (this._recaptchaWidgetId != null) {
      resetRecaptchaWidget(this._recaptchaWidgetId);
      return;
    }
    this._recaptchaWidgetId = renderRecaptchaWidget(this._recaptchaHost, siteKey);
  }

  protected renderHtml() {
    if (!this._open) return html``;
    const choosingCompany = this._companies.length > 0;
    return html`
      <div class="overlay" @click=${() => this._onCancel()} @keydown=${(e: KeyboardEvent) => this._onKeydown(e)}>
        <div
          class="panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sugar-login-title"
          @click=${(e: Event) => e.stopPropagation()}
        >
          <div class="header">
            <div>
              <h2 class="title" id="sugar-login-title">${t("authLoginTitle")}</h2>
              <p class="subtitle">
                ${choosingCompany ? t("authSelectCompany") : t("authLoginSubtitle")}
              </p>
            </div>
            <button type="button" class="close" aria-label=${t("close")} @click=${() => this._onCancel()}>
              ×
            </button>
          </div>
          ${
            choosingCompany
              ? html`
                  <div class="companies">
                    ${this._companies.map(
                      (company) => html`
                        <button
                          type="button"
                          class="company"
                          ?disabled=${this._submitting || company.available === false}
                          @click=${() => this._pickCompany(company.companyId)}
                        >
                          ${company.name}
                        </button>
                      `,
                    )}
                  </div>
                  <div class="actions">
                    <button type="button" class="btn btn-secondary" ?disabled=${this._submitting} @click=${() => this._onCancel()}>
                      ${t("authCancel")}
                    </button>
                  </div>
                `
              : html`
                  <form class="fields" @submit=${(e: Event) => this._onSubmit(e)}>
                    <label>
                      ${t("authEmail")}
                      <input
                        type="text"
                        name="username"
                        autocomplete="username"
                        .value=${this._identifier}
                        @input=${(e: Event) => {
                          this._identifier = (e.target as HTMLInputElement).value;
                        }}
                      />
                    </label>
                    <label>
                      ${t("authPassword")}
                      <input
                        type="password"
                        name="password"
                        autocomplete="current-password"
                        .value=${this._password}
                        @input=${(e: Event) => {
                          this._password = (e.target as HTMLInputElement).value;
                        }}
                      />
                    </label>
                    ${this._recaptchaSiteKey
                      ? html`<div class="recaptcha"><slot name="recaptcha"></slot></div>`
                      : nothing}
                    ${this._error ? html`<p class="error">${this._error}</p>` : nothing}
                    <div class="actions">
                      <button type="button" class="btn btn-secondary" ?disabled=${this._submitting} @click=${() => this._onCancel()}>
                        ${t("authCancel")}
                      </button>
                      <button type="submit" class="btn btn-primary" ?disabled=${this._submitting}>
                        ${this._submitting ? t("authSigningIn") : t("authSignIn")}
                      </button>
                    </div>
                  </form>
                `
          }
        </div>
      </div>
    `;
  }
}
